import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { caissesApi } from '../../services/api';
import type { Caisse } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import Table from '../../components/Table';
import { Search, Plus, Edit2, Trash2, Building2, MapPin } from 'lucide-react';
import CaisseModal from './CaisseModal';

const CaisseList: React.FC = () => {
  const notify = useNotification();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCaisse, setEditingCaisse] = useState<Caisse | null>(null);
  const [deletingCaisse, setDeletingCaisse] = useState<Caisse | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch caisses
  const { data, isLoading } = useQuery({
    queryKey: ['caisses', debouncedSearch, page, perPage],
    queryFn: async () => {
      const response = await caissesApi.getAll({
        search: debouncedSearch || undefined,
        page,
        per_page: perPage,
      });
      return response.data;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => caissesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caisses'] });
      setDeletingCaisse(null);
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message || 'Erreur lors de la suppression';
      notify.error(message);
    },
  });

  const handleEdit = (caisse: Caisse) => {
    setEditingCaisse(caisse);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingCaisse(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingCaisse(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['caisses'] });
    handleModalClose();
  };

  const columns = [
    {
      header: 'Caisse',
      accessor: (row: Caisse) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900 max-w-md truncate">
              {row.caisse}
            </div>
            {row.address && (
              <div className="text-sm text-gray-500 truncate max-w-md">
                {row.address}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Localisation',
      accessor: (row: Caisse) => (
        <div className="flex items-center gap-2">
          {(row.npa || row.localite) ? (
            <>
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-900">
                {[row.npa, row.localite].filter(Boolean).join(' ')}
              </span>
            </>
          ) : (
            <span className="text-sm text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      header: 'Formulaire',
      accessor: (row: Caisse) => {
        const value = row.formulaire_instruction as unknown;
        const isTrue = value === true || value === 1 || value === '1';
        return (
          <div className="flex items-center gap-2">
            {isTrue ? (
              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                Oui
              </span>
            ) : (
              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                Non
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: 'Actions',
      accessor: (row: Caisse) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleEdit(row)}
            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Modifier"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setDeletingCaisse(row)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Caisses de pension</h1>
          <p className="text-gray-600 mt-1">Gestion des institutions de prévoyance</p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
        >
          <Plus className="w-5 h-5" />
          Nouvelle caisse
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, adresse, NPA ou localité..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(parseInt(e.target.value));
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="10">10 par page</option>
            <option value="15">15 par page</option>
            <option value="25">25 par page</option>
            <option value="50">50 par page</option>
            <option value="100">100 par page</option>
          </select>
        </div>
      </div>

      <Table
        data={data?.data || []}
        columns={columns}
        loading={isLoading}
        emptyMessage="Aucune caisse trouvée"
        pagination={
          data?.meta
            ? {
                currentPage: data.meta.current_page,
                totalPages: data.meta.last_page,
                total: data.meta.total,
                from: data.meta.from,
                to: data.meta.to,
                onPageChange: setPage,
              }
            : undefined
        }
      />

      {/* Caisse Modal */}
      {isModalOpen && (
        <CaisseModal
          caisse={editingCaisse}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingCaisse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer la caisse{' '}
              <span className="font-medium">{deletingCaisse.caisse}</span> ?
              Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingCaisse(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => deleteMutation.mutate(deletingCaisse.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaisseList;
